/**
 * Transformer for diff fences that use more than 3 backticks.
 *
 * Lexical's built-in CODE transformer recognises exactly 3-backtick fences via
 * CODE_START_REGEX = /^[ \t]*```(\w+)?/.  For a 4+-backtick opening like
 * ````diff, the regex still matches the first 3 backticks but captures
 * `undefined` for the language (the 4th backtick is not a \w character).
 * DiffHunkPlugin then skips the resulting CodeNode because
 * `node.getLanguage() !== 'diff'`.
 *
 * This transformer is registered before Lexical's CODE transformer in
 * LOAD_TRANSFORMERS.  Its regExpStart only matches fences with 4+ backticks
 * followed by "diff", so it doesn't interfere with ordinary 3-backtick fences
 * or non-diff code blocks.
 *
 * `handleImportAfterStartMatch` takes over after the start match and
 * manually scans for the closing fence (same backtick count as the opener),
 * then creates a DiffHunkNode directly — no CodeNode or DiffHunkPlugin needed.
 */

import type { MultilineElementTransformer } from '@lexical/markdown';
import {
  $createDiffHunkNode,
  $isDiffHunkNode,
  DiffHunkNode,
} from '../customNodes/DiffHunkNode/DiffHunkNode';

/**
 * Matches an opening fence of 4 or more backticks followed by "diff".
 * Leading whitespace (spaces/tabs) is allowed, mirroring Lexical's own
 * CODE_START_REGEX which also permits indented fences.
 */
const LONG_DIFF_FENCE_START = /^[ \t]*(`{4,})diff[ \t]*$/;

export const LONG_DIFF_HUNK: MultilineElementTransformer = {
  get dependencies() {
    return [DiffHunkNode];
  },

  regExpStart: LONG_DIFF_FENCE_START,

  handleImportAfterStartMatch: ({
    lines,
    rootNode,
    startLineIndex,
    startMatch,
  }) => {
    const fenceLen = startMatch[1].length;
    const closingFenceRegex = new RegExp(`^[ \\t]*\`{${fenceLen}}[ \\t]*$`);

    const bodyLines: string[] = [];
    let endLineIndex = startLineIndex + 1;

    while (endLineIndex < lines.length) {
      if (closingFenceRegex.test(lines[endLineIndex])) {
        break;
      }
      bodyLines.push(lines[endLineIndex]);
      endLineIndex++;
    }

    // If we reached EOF without finding the closing fence, bail out and let
    // other transformers handle the line. Malformed / truncated markdown
    // should not silently absorb the rest of the document into a hunk.
    if (endLineIndex >= lines.length) {
      return null;
    }

    const diffContent = bodyLines.join('\n');
    const diffHunkNode = $createDiffHunkNode(diffContent);
    rootNode.append(diffHunkNode);

    return [true, endLineIndex];
  },

  replace: () => {
    // Not called: handleImportAfterStartMatch handles all import cases.
  },

  export: node => {
    if (!$isDiffHunkNode(node)) {
      return null;
    }
    // Export always uses 3-backtick format (standard round-trip).
    return `\`\`\`diff\n${node.__diffContent}\n\`\`\``;
  },

  type: 'multiline-element',
};
